/**
 * adminAuth — single-source helper for the admin JWT token.
 *
 * Migrated from .js → .ts (Sprint 5 TS migration). Behaviour unchanged.
 *
 * Why sessionStorage instead of localStorage?
 *   - localStorage persists FOREVER and is readable by any script on
 *     the same origin. A successful XSS injection can read it and
 *     pivot from there to admin-grade calls.
 *   - sessionStorage is scoped to the browser tab/session: closing
 *     the tab logs the admin out automatically. This is the security
 *     default recommended by OWASP for short-lived auth tokens that
 *     cannot be moved to httpOnly cookies (our backend currently
 *     accepts JWT in Authorization headers, so cookies aren't an
 *     option without a wider refactor).
 *
 * Migration: this module also reads-and-clears any LEGACY token left
 * in localStorage on module load so existing admins don't get a hard
 * logout the moment we ship this change.
 */

import { logger } from "@/lib/logger";

const TOKEN_KEY = "deepotus_admin_token";
const LEGACY_TOKEN_KEY = "deepotus_admin_token";

let migrated = false;

function migrateLegacyToken(): void {
  if (migrated || typeof window === "undefined") return;
  migrated = true;
  try {
    const legacy = window.localStorage.getItem(LEGACY_TOKEN_KEY);
    if (legacy && !window.sessionStorage.getItem(TOKEN_KEY)) {
      window.sessionStorage.setItem(TOKEN_KEY, legacy);
    }
    // Always strip from localStorage so persisted copies don't linger,
    // even when the legacy value was empty.
    window.localStorage.removeItem(LEGACY_TOKEN_KEY);
  } catch (e) {
    // Storage blocked (private mode, quota, disabled) — non-fatal, log for
    // dev visibility only. Falls back to an in-memory session (the admin
    // will simply be asked to re-authenticate on the next page).
    logger.debug("[adminAuth] legacy token migration skipped:", e);
  }
}

// Run once at module load so the cleanup happens even if the first
// caller writes a fresh token via `setAdminToken` (login flow) before
// any `getAdminToken` read.
migrateLegacyToken();

export function getAdminToken(): string {
  if (typeof window === "undefined") return "";
  migrateLegacyToken();
  try {
    return window.sessionStorage.getItem(TOKEN_KEY) || "";
  } catch (e) {
    logger.debug("[adminAuth] getAdminToken failed (storage blocked):", e);
    return "";
  }
}

export function setAdminToken(token: string): void {
  if (typeof window === "undefined") return;
  try {
    if (token) window.sessionStorage.setItem(TOKEN_KEY, token);
    else window.sessionStorage.removeItem(TOKEN_KEY);
  } catch (e) {
    logger.debug("[adminAuth] setAdminToken failed (storage blocked):", e);
  }
}

export function clearAdminToken(): void {
  setAdminToken("");
}

export const ADMIN_TOKEN_KEY = TOKEN_KEY;
